<script setup lang="ts">
  import { Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { FieldSize } from '../model/types';

  type FieldVariant = 'input' | 'display';

  interface FieldProps {
    label: string;
    active?: boolean;
    disabled?: boolean;
    variant?: FieldVariant;
    valueTestid?: string;
    size?: `${FieldSize}`;
  }

  const props = withDefaults(defineProps<FieldProps>(), {
    active: false,
    disabled: false,
    variant: 'display',
    valueTestid: undefined,
    size: FieldSize.M,
  });

  interface FieldEmits {
    (e: 'click', event: MouseEvent): void;
  }

  const emit = defineEmits<FieldEmits>();

  const onClick = (event: MouseEvent): void => {
    emit('click', event);
  };
</script>

<template>
  <div
    class="field__control"
    :class="[
      `field__control_${props.variant}`,
      `field__control_size-${size}`,
      {
        field__control_active: active,
        field__control_disabled: disabled,
      },
    ]"
    @click="onClick"
  >
    <Typo
      class="field__label"
      :variant="TypoVariant.BodySRegular"
      tag="span"
    >
      {{ label }}
    </Typo>

    <div
      class="field__value"
      :data-testid="valueTestid"
    >
      <slot name="value" />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .field {
    $root: &;

    &__control {
      background: var(--color-bg-primary);
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xxxs);
      justify-content: space-between;
      padding: var(--spacing-xs) var(--spacing-s);
      transition: background-color var(--transition-default);

      &_input {
        cursor: text;
      }

      &_display {
        box-sizing: border-box;
        cursor: pointer;
        height: 54px;
        justify-content: space-between;

        #{$root}__value {
          color: var(--color-text-primary);
          white-space: nowrap;
        }
      }

      &_size-l {
        gap: var(--spacing-xxs);
        padding: var(--spacing-s);

        &#{$root}__control_display {
          height: 64px;
        }
      }

      &_active {
        background: var(--color-disabled-100);
      }

      &_disabled {
        background: var(--color-disabled-100);

        #{$root}__label {
          color: var(--color-disabled-900);
        }
      }
    }

    &__label {
      color: var(--color-text-secondary);
      pointer-events: none;
      user-select: none;
    }
  }
</style>
